const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

/** Table spans columns D (4) through L (12) */
const COL_NUM = 4;
const COL_LABEL = 5;
const COL_VALUE_START = 6;
const COL_VALUE_END = 12;
const COL_LAST = 12;

/** Three-column data splits within F–L */
const COL_DATA_1_END = 8;
const COL_DATA_2_START = 9;
const COL_DATA_2_END = 10;
const COL_DATA_3_START = 11;

/** Two-column FY date splits within F–L */
const COL_FY_START_END = 9;
const COL_FY_END_START = 10;

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
};

const safeNum = (value, fallback = "0") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(2);
};

const formatPct = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const text = String(value);
  return text.includes("%") ? text : `${text}%`;
};

function parsePct(value) {
  if (value === null || value === undefined || value === "") return NaN;
  return Number(String(value).replace("%", "").trim());
}

function getTurnoverOtherPct(obj) {
  if (obj?.other !== null && obj?.other !== undefined && obj?.other !== "") {
    return formatPct(obj.other);
  }
  const total = parsePct(obj?.total);
  const male = parsePct(obj?.male);
  const female = parsePct(obj?.female);
  if (!Number.isNaN(total) && !Number.isNaN(male) && !Number.isNaN(female)) {
    const other = Math.max(0, total - male - female);
    return formatPct(other.toFixed(2));
  }
  return formatPct(0);
}

function getOtherGender(obj) {
  const explicitCount = obj?.othergender ?? obj?.otherGender ?? obj?.genderOther;
  const explicitPct = obj?.otherpercentage ?? obj?.otherPercentage ?? obj?.genderOtherpercentage;

  if (explicitCount !== null && explicitCount !== undefined && explicitCount !== "") {
    return {
      count: safeNum(explicitCount),
      pct: formatPct(explicitPct ?? 0),
    };
  }

  const total = Number(obj?.total);
  const male = Number(obj?.male);
  const female = Number(obj?.female);

  if (Number.isNaN(total) || total <= 0) {
    return { count: "0", pct: "0.00%" };
  }

  const other = Math.max(0, total - (Number.isNaN(male) ? 0 : male) - (Number.isNaN(female) ? 0 : female));
  const pct = ((other / total) * 100).toFixed(2);
  return { count: String(other), pct: `${pct}%` };
}

const formatIndianDate = (day, month, year) => {
  const dd = String(day).padStart(2, "0");
  const mm = String(month).padStart(2, "0");
  return `${dd}-${mm}-${year}`;
};

const getFinancialYearRanges = (financialYear) => {
  const year = Number(financialYear);
  if (!year) {
    return {
      current: { label: "Current Financial Year", start: "", end: "" },
      previous: { label: "Previous Financial Year", start: "", end: "" },
      prior: { label: "Prior to Previous Financial year", start: "", end: "" },
    };
  }

  return {
    current: {
      label: "Current Financial Year",
      start: formatIndianDate(1, 4, year - 1),
      end: formatIndianDate(31, 3, year),
    },
    previous: {
      label: "Previous Financial Year",
      start: formatIndianDate(1, 4, year - 2),
      end: formatIndianDate(31, 3, year - 1),
    },
    prior: {
      label: "Prior to Previous Financial year",
      start: formatIndianDate(1, 4, year - 3),
      end: formatIndianDate(31, 3, year - 2),
    },
  };
};

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function formatFyShort(endYear) {
  const fy = Number(endYear);
  if (!fy) return "";
  return `${fy - 1}-${String(fy).slice(-2)}`;
}

function addPeachPointHeader(ws, row, number, label, mergeEnd = COL_VALUE_END) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = { ...BODY_FONT, bold: true };
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  ws.mergeCells(row, COL_LABEL, row, mergeEnd);
  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.value = label;
  labelCell.font = { ...BODY_FONT, bold: true };
  borderCell(labelCell);
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  return row + 1;
}

function setRowHeightForWrappedText(ws, row, text) {
  const lines = String(text).split("\n").length;
  const estimatedWrap = Math.ceil(String(text).replace(/\n/g, "").length / 42);
  ws.getRow(row).height = Math.max(15, Math.max(lines, estimatedWrap) * 15);
}

function addLetteredLinkRow(ws, row, number, letter, label, linkText, sheetLink) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "top", wrapText: true };

  const letterCell = ws.getCell(row, COL_LABEL);
  letterCell.value = letter;
  letterCell.font = BODY_FONT;
  borderCell(letterCell);
  letterCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };

  ws.mergeCells(row, COL_VALUE_START, row, COL_VALUE_END - 1);
  const labelCell = ws.getCell(row, COL_VALUE_START);
  labelCell.value = label;
  labelCell.font = BODY_FONT;
  borderCell(labelCell);
  labelCell.alignment = { horizontal: "left", vertical: "top", wrapText: true };

  const linkCell = ws.getCell(row, COL_VALUE_END);
  const sheetName = sheetLink.replace(/^'|'$/g, "");
  linkCell.value = {
    text: linkText,
    hyperlink: `#'${sheetName}'!D3`,
  };
  linkCell.font = LINK_FONT;
  borderCell(linkCell);
  linkCell.alignment = { horizontal: "center", vertical: "top", wrapText: true };

  setRowHeightForWrappedText(ws, row, label);

  return row + 1;
}

function addSubPointRow(ws, row, number, letter, label, value) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  const letterCell = ws.getCell(row, COL_LABEL);
  letterCell.value = letter;
  letterCell.font = BODY_FONT;
  borderCell(letterCell);
  letterCell.alignment = { horizontal: "left", vertical: "middle" };

  ws.mergeCells(row, COL_VALUE_START, row, COL_FY_START_END);
  const labelCell = ws.getCell(row, COL_VALUE_START);
  labelCell.value = label;
  labelCell.font = BODY_FONT;
  borderCell(labelCell);
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  ws.mergeCells(row, COL_FY_END_START, row, COL_VALUE_END);
  const valueCell = ws.getCell(row, COL_FY_END_START);
  valueCell.value = value;
  valueCell.font = BODY_FONT;
  borderCell(valueCell);
  valueCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  return row + 1;
}

function styleSectionHeader(ws, row, title) {
  ws.mergeCells(row, COL_NUM, row, COL_LAST);
  const cell = ws.getCell(row, COL_NUM);
  cell.value = title;
  cell.font = { name: "Calibri", size: 11, bold: true };
  cell.fill = HEADER_FILL;
  borderCell(cell);
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
}

function styleSubSectionHeader(ws, row, roman, title, mergeEnd = COL_LAST) {
  const romanCell = ws.getCell(row, COL_NUM);
  romanCell.value = roman;
  romanCell.font = { name: "Calibri", size: 11, bold: true };
  romanCell.fill = SUBHEADER_FILL;
  borderCell(romanCell);
  romanCell.alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(row, COL_LABEL, row, mergeEnd);
  const titleCell = ws.getCell(row, COL_LABEL);
  titleCell.value = title;
  titleCell.font = { name: "Calibri", size: 11, bold: true };
  titleCell.fill = SUBHEADER_FILL;
  borderCell(titleCell);
  titleCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
}

function setLabelCell(ws, row, label, mandatory = false) {
  const cell = ws.getCell(row, COL_LABEL);
  if (mandatory) {
    cell.value = {
      richText: [
        { text: label, font: { ...BODY_FONT, color: { argb: "FF000000" } } },
        { text: "*", font: { ...BODY_FONT, color: { argb: "FFFF0000" } } },
      ],
    };
  } else {
    cell.value = label;
    cell.font = BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
}

function setValueCell(ws, row, value, options = {}) {
  const { link = null, align = "left" } = options;
  ws.mergeCells(row, COL_VALUE_START, row, COL_VALUE_END);
  const cell = ws.getCell(row, COL_VALUE_START);

  if (link) {
    const sheetName = link.replace(/^'|'$/g, "");
    cell.value = {
      text: value || "View",
      hyperlink: `#'${sheetName}'!D3`,
    };
    cell.font = LINK_FONT;
    cell.alignment = { horizontal: "center", vertical: "middle" };
  } else {
    cell.value = value;
    cell.font = BODY_FONT;
    cell.alignment = { horizontal: align, vertical: "middle", wrapText: true };
  }

  borderCell(cell);
}

function addLabelValueRow(ws, row, number, label, value, options = {}) {
  const { mandatory = false, link = null, valueAlign = "left" } = options;

  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  setLabelCell(ws, row, label, mandatory);
  setValueCell(ws, row, value, { link, align: valueAlign });

  return row + 1;
}

function addFullWidthLabelRow(ws, row, label, number = "", mergeEnd = COL_VALUE_END) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  ws.mergeCells(row, COL_LABEL, row, mergeEnd);
  const cell = ws.getCell(row, COL_LABEL);
  cell.value = label;
  cell.font = BODY_FONT;
  borderCell(cell);
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  return row + 1;
}

function addSubLabelValueRow(ws, row, label, value) {
  const numCell = ws.getCell(row, COL_NUM);
  borderCell(numCell);

  setLabelCell(ws, row, label);
  setValueCell(ws, row, value);

  return row + 1;
}

function addFinancialYearBlock(ws, row, fyRanges) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = "13.";
  numCell.font = BODY_FONT;
  borderCell(numCell);
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  setLabelCell(ws, row, "Financial year for which reporting is being done");
  setValueCell(ws, row, "");
  row += 1;

  const emptyNum = ws.getCell(row, COL_NUM);
  borderCell(emptyNum);
  borderCell(ws.getCell(row, COL_LABEL));

  ws.mergeCells(row, COL_VALUE_START, row, COL_FY_START_END);
  const startHeader = ws.getCell(row, COL_VALUE_START);
  startHeader.value = "Start date";
  startHeader.font = { ...BODY_FONT, bold: true };
  borderCell(startHeader);
  startHeader.alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(row, COL_FY_END_START, row, COL_VALUE_END);
  const endHeader = ws.getCell(row, COL_FY_END_START);
  endHeader.value = "End date";
  endHeader.font = { ...BODY_FONT, bold: true };
  borderCell(endHeader);
  endHeader.alignment = { horizontal: "center", vertical: "middle" };
  row += 1;

  [fyRanges.current, fyRanges.previous, fyRanges.prior].forEach((fy) => {
    borderCell(ws.getCell(row, COL_NUM));
    setLabelCell(ws, row, fy.label);

    ws.mergeCells(row, COL_VALUE_START, row, COL_FY_START_END);
    const startCell = ws.getCell(row, COL_VALUE_START);
    startCell.value = fy.start;
    startCell.font = BODY_FONT;
    borderCell(startCell);
    startCell.alignment = { horizontal: "center", vertical: "middle" };

    ws.mergeCells(row, COL_FY_END_START, row, COL_VALUE_END);
    const endCell = ws.getCell(row, COL_FY_END_START);
    endCell.value = fy.end;
    endCell.font = BODY_FONT;
    borderCell(endCell);
    endCell.alignment = { horizontal: "center", vertical: "middle" };

    row += 1;
  });

  return row;
}

/** Employee tables use columns E–L; turnover table extends to Q */
const EMP_COL_END = 12;
const TURNOVER_FY_COLS = 4;
const TURNOVER_TABLE_END = 17;

function setTableCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align || "center",
    vertical: "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function addWorkforceSectionTitle(ws, row, letter, label) {
  const letterCell = ws.getCell(row, COL_NUM);
  letterCell.value = letter;
  letterCell.font = { ...BODY_FONT, bold: true };
  letterCell.fill = HEADER_FILL;
  borderCell(letterCell);
  letterCell.alignment = { horizontal: "right", vertical: "middle" };

  ws.mergeCells(row, COL_LABEL, row, EMP_COL_END);
  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.value = label;
  labelCell.font = { ...BODY_FONT, bold: true };
  labelCell.fill = HEADER_FILL;
  borderCell(labelCell);
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  return row + 1;
}

function addWorkforceTableHeader(ws, row) {
  const hFill = TABLE_HEADER_FILL;
  const r1 = row;
  const r2 = row + 1;

  ws.mergeCells(r1, COL_NUM, r2, COL_NUM);
  setTableCell(ws, r1, COL_NUM, "Sr. No.", { bold: true, fill: hFill });

  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  setTableCell(ws, r1, COL_LABEL, "Particulars", { bold: true, align: "left", fill: hFill });

  ws.mergeCells(r1, 6, r2, 6);
  setTableCell(ws, r1, 6, "Total(A)", { bold: true, fill: hFill });

  ws.mergeCells(r1, 7, r1, 8);
  setTableCell(ws, r1, 7, "Male", { bold: true, fill: hFill });

  ws.mergeCells(r1, 9, r1, 10);
  setTableCell(ws, r1, 9, "Female", { bold: true, fill: hFill });

  ws.mergeCells(r1, 11, r1, EMP_COL_END);
  setTableCell(ws, r1, 11, "Other", { bold: true, fill: hFill });

  setTableCell(ws, r2, 7, "No. (B)", { bold: true, fill: hFill });
  setTableCell(ws, r2, 8, "% (B / A)", { bold: true, fill: hFill });
  setTableCell(ws, r2, 9, "No. (C)", { bold: true, fill: hFill });
  setTableCell(ws, r2, 10, "% (C / A)", { bold: true, fill: hFill });
  setTableCell(ws, r2, 11, "No. (H)", { bold: true, fill: hFill });
  setTableCell(ws, r2, EMP_COL_END, "% (H / A)", { bold: true, fill: hFill });

  return r2 + 1;
}

function addWorkforceGroupBanner(ws, row, title) {
  ws.mergeCells(row, COL_NUM, row, COL_LABEL);
  const cell = ws.getCell(row, COL_NUM);
  cell.value = title;
  cell.font = { ...BODY_FONT, bold: true };
  borderCell(cell);
  cell.alignment = { horizontal: "center", vertical: "middle" };

  for (let col = 6; col <= EMP_COL_END; col++) {
    const emptyCell = ws.getCell(row, col);
    emptyCell.value = "";
    borderCell(emptyCell);
  }

  return row + 1;
}

function addWorkforceDataRow(ws, row, srNo, label, obj) {
  const other = getOtherGender(obj);

  setTableCell(ws, row, COL_NUM, String(srNo), { align: "center" });
  setTableCell(ws, row, COL_LABEL, label, { align: "left" });
  setTableCell(ws, row, 6, safeNum(obj?.total), { align: "right" });
  setTableCell(ws, row, 7, safeNum(obj?.male), { align: "right" });
  setTableCell(ws, row, 8, formatPct(obj?.malepercentage), { align: "right" });
  setTableCell(ws, row, 9, safeNum(obj?.female), { align: "right" });
  setTableCell(ws, row, 10, formatPct(obj?.femalepercentage), { align: "right" });
  setTableCell(ws, row, 11, other.count, { align: "right" });
  setTableCell(ws, row, EMP_COL_END, other.pct, { align: "right" });
  return row + 1;
}

function addWorkforceGroup(ws, row, bannerTitle, node, rowDefs, startSr) {
  row = addWorkforceGroupBanner(ws, row, bannerTitle);
  let sr = startSr;

  rowDefs.forEach(({ label, key }) => {
    row = addWorkforceDataRow(ws, row, sr, label, node?.[key]);
    sr += 1;
  });

  return { row, nextSr: sr };
}

function addWomenRepTable(ws, row, womenRep) {
  borderCell(ws.getCell(row, COL_NUM));
  setTableCell(ws, row, COL_LABEL, "Particulars", { bold: true, align: "left" });
  setTableCell(ws, row, 6, "Total (A)", { bold: true });
  setTableCell(ws, row, 7, "No. (B)", { bold: true });
  setTableCell(ws, row, 8, "% (B / A)", { bold: true });
  row += 1;

  const items = [
    { label: "Board of Directors", obj: womenRep?.boardOfDirectors },
    { label: "Key Management Personnel", obj: womenRep?.keyManagementPersonnel },
  ];

  items.forEach(({ label, obj }) => {
    borderCell(ws.getCell(row, COL_NUM));
    setTableCell(ws, row, COL_LABEL, label, { align: "left" });
    setTableCell(ws, row, 6, safeNum(obj?.total), { align: "right" });
    setTableCell(ws, row, 7, safeNum(obj?.female), { align: "right" });
    setTableCell(ws, row, 8, formatPct(obj?.femalepercentage), { align: "right" });
    row += 1;
  });

  return row;
}

function addTableBanner(ws, row, title) {
  borderCell(ws.getCell(row, COL_NUM));
  ws.mergeCells(row, COL_LABEL, row, EMP_COL_END);
  const cell = ws.getCell(row, COL_LABEL);
  cell.value = title;
  cell.font = { ...BODY_FONT, bold: true };
  cell.fill = SUBHEADER_FILL;
  borderCell(cell);
  cell.alignment = { horizontal: "left", vertical: "middle" };
  return row + 1;
}

function addTurnoverRateTable(ws, row, financialYear, turnoverRate) {
  const fy = Number(financialYear) || new Date().getFullYear();
  const hFill = TABLE_HEADER_FILL;
  const periods = [
    {
      label: `Turnover rate in current FY (${formatFyShort(fy)})`,
      node: turnoverRate?.currentfy,
    },
    {
      label: `Turnover rate in previous FY (${formatFyShort(fy - 1)})`,
      node: turnoverRate?.previousfy,
    },
    {
      label: `Turnover rate in the year prior to the previous FY (${formatFyShort(fy - 2)})`,
      node: turnoverRate?.priorfy,
    },
  ];

  const r1 = row;
  const r2 = row + 1;

  borderCell(ws.getCell(r1, COL_NUM));
  borderCell(ws.getCell(r2, COL_NUM));
  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  setTableCell(ws, r1, COL_LABEL, "Particulars", { bold: true, align: "left", fill: hFill });

  periods.forEach((period, idx) => {
    const startCol = 6 + idx * TURNOVER_FY_COLS;
    const endCol = startCol + TURNOVER_FY_COLS - 1;
    ws.mergeCells(r1, startCol, r1, endCol);
    setTableCell(ws, r1, startCol, period.label, { bold: true, fill: hFill });
    ["Male", "Female", "Other", "Total"].forEach((sub, i) => {
      setTableCell(ws, r2, startCol + i, sub, { bold: true, fill: hFill });
    });
  });

  row = r2 + 1;

  [
    { label: "Permanent Employees", key: "employees" },
    { label: "Permanent Workers", key: "workers" },
  ].forEach(({ label, key }) => {
    borderCell(ws.getCell(row, COL_NUM));
    setTableCell(ws, row, COL_LABEL, label, { align: "left", fill: hFill });

    periods.forEach((period, idx) => {
      const obj = period.node?.[key];
      const startCol = 6 + idx * TURNOVER_FY_COLS;
      setTableCell(ws, row, startCol, formatPct(obj?.male), { align: "right" });
      setTableCell(ws, row, startCol + 1, formatPct(obj?.female), { align: "right" });
      setTableCell(ws, row, startCol + 2, getTurnoverOtherPct(obj), { align: "right" });
      setTableCell(ws, row, startCol + 3, formatPct(obj?.total), { align: "right" });
    });

    row += 1;
  });

  return row;
}

function addEmployeesSection(ws, row, employeesAndWorkers, financialYear) {
  const fy = Number(financialYear) || new Date().getFullYear();

  row += 1;
  styleSubSectionHeader(ws, row, "IV.", "Employees", EMP_COL_END);
  row += 1;

  row = addFullWidthLabelRow(
    ws,
    row,
    "Details as at the end of Financial Year",
    "25.",
    EMP_COL_END
  );
  row = addWorkforceSectionTitle(
    ws,
    row,
    "A.",
    "Employees and workers (including differently abled):"
  );
  row = addWorkforceTableHeader(ws, row);

  let sr = 1;
  ({ row, nextSr: sr } = addWorkforceGroup(ws, row, "EMPLOYEES", employeesAndWorkers?.emp, [
    { label: "Permanent (D)", key: "permanent" },
    { label: "Other than permanent (E)", key: "other" },
    { label: "Total employees (D + E)", key: "total" },
  ], sr));

  ({ row } = addWorkforceGroup(ws, row, "WORKERS", employeesAndWorkers?.worker, [
    { label: "Permanent (F)", key: "permanent" },
    { label: "Other than permanent (G)", key: "other" },
    { label: "Total workers (F + G)", key: "total" },
  ], sr));

  row = addWorkforceSectionTitle(
    ws,
    row,
    "B.",
    "Differently abled Employees and workers:"
  );
  row = addWorkforceTableHeader(ws, row);

  sr = 1;
  ({ row, nextSr: sr } = addWorkforceGroup(
    ws,
    row,
    "DIFFERENTLY ABLED EMPLOYEES",
    employeesAndWorkers?.differentlyabled?.employees,
    [
      { label: "Permanent (D)", key: "permanent" },
      { label: "Other than permanent (E)", key: "other" },
      { label: "Total differently abled employees (D + E)", key: "total" },
    ],
    sr
  ));

  ({ row } = addWorkforceGroup(
    ws,
    row,
    "DIFFERENTLY ABLED WORKERS",
    employeesAndWorkers?.differentlyabled?.workers,
    [
      { label: "Permanent (F)", key: "permanent" },
      { label: "Other than permanent (G)", key: "other" },
      { label: "Total differently abled workers (F + G)", key: "total" },
    ],
    sr
  ));

  row = addFullWidthLabelRow(
    ws,
    row,
    "Participation/Inclusion/Representation of women",
    "26."
  );
  row = addWomenRepTable(ws, row, employeesAndWorkers?.womenRepresentation);

  row = addPeachPointHeader(
    ws,
    row,
    "27.",
    "(Disclose trends for the past 3 years)",
    TURNOVER_TABLE_END
  );
  row = addTurnoverRateTable(ws, row, fy, employeesAndWorkers?.turnoverRate);

  return row;
}

const COMPLAINTS_TABLE_END = 13;
const STAKEHOLDER_GROUPS = [
  ["communities", "Communities"],
  ["investors", "Investors"],
  ["shareHolders", "Shareholders"],
  ["employeesAndWorkers", "Employees and Workers"],
  ["customers", "Customers"],
  ["valueChainParnter", "Value Chain Partner"],
  ["other", "Other"],
];

function addComplaintsTable(ws, row, financialYear, currentFY, previousFY) {
  const fy = Number(financialYear) || new Date().getFullYear();
  const hFill = TABLE_HEADER_FILL;
  const r1 = row;
  const r2 = row + 1;

  borderCell(ws.getCell(r1, COL_NUM));
  borderCell(ws.getCell(r2, COL_NUM));

  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  setTableCell(ws, r1, COL_LABEL, "Stakeholder group from whom complaint is received", {
    bold: true,
    align: "left",
    fill: hFill,
  });

  ws.mergeCells(r1, 6, r2, 7);
  setTableCell(
    ws,
    r1,
    6,
    "Grievance Redressal Mechanism in Place (If Yes, then provide web-link for grievance redress policy)",
    { bold: true, fill: hFill }
  );

  ws.mergeCells(r1, 8, r1, 10);
  setTableCell(ws, r1, 8, `FY ${formatFyShort(fy)} (Current Financial Year)`, {
    bold: true,
    fill: hFill,
  });

  ws.mergeCells(r1, 11, r1, COMPLAINTS_TABLE_END);
  setTableCell(ws, r1, 11, `FY ${formatFyShort(fy - 1)} (Previous Financial Year)`, {
    bold: true,
    fill: hFill,
  });

  [
    { col: 8, label: "Number of complaints filed during the year" },
    { col: 9, label: "Number of complaints pending resolution at close of the year" },
    { col: 10, label: "Remarks" },
    { col: 11, label: "Number of complaints filed during the year" },
    { col: 12, label: "Number of complaints pending resolution at close of the year" },
    { col: 13, label: "Remarks" },
  ].forEach(({ col, label }) => {
    setTableCell(ws, r2, col, label, { bold: true, fill: hFill });
  });

  row = r2 + 1;

  STAKEHOLDER_GROUPS.forEach(([key, label]) => {
    const current = currentFY?.[key] || {};
    const previous = previousFY?.[key] || {};

    borderCell(ws.getCell(row, COL_NUM));
    setTableCell(ws, row, COL_LABEL, label, { align: "left" });
    ws.mergeCells(row, 6, row, 7);
    setTableCell(ws, row, 6, safeText(current?.grievanceRedressalWeblink), { align: "left" });
    setTableCell(ws, row, 8, safeNum(current?.filed), { align: "right" });
    setTableCell(ws, row, 9, safeNum(current?.pending), { align: "right" });
    setTableCell(ws, row, 10, safeText(current?.remarks), { align: "left" });
    setTableCell(ws, row, 11, safeNum(previous?.filed), { align: "right" });
    setTableCell(ws, row, 12, safeNum(previous?.pending), { align: "right" });
    setTableCell(ws, row, 13, safeText(previous?.remarks), { align: "left" });
    row += 1;
  });

  return row;
}

function addRemainingDisclosuresSection(ws, row, financialYear, holding, transparency) {
  styleSubSectionHeader(
    ws,
    row,
    "V.",
    "Holding, Subsidiary and Associate Companies (including joint ventures)"
  );
  row += 1;

  row = addLetteredLinkRow(
    ws,
    row,
    "28.",
    "(a)",
    "Names of holding / subsidiary / associate\ncompanies / joint ventures",
    "View details",
    "'Holding,subsidiary or associate'"
  );

  styleSubSectionHeader(ws, row, "VI.", "CSR Details");
  row += 1;

  const csr = holding?.csrDetails || {};
  row = addSubPointRow(
    ws,
    row,
    "29.",
    "(i)",
    "Whether CSR is applicable as per section 135 of Companies Act, 2013",
    csr?.applicable ? "Yes" : "No"
  );
  row = addSubPointRow(ws, row, "", "(ii)", "Turnover (in Rs.)", formatAmount(csr?.turnover));
  row = addSubPointRow(ws, row, "", "(iii)", "Net worth (in Rs.)", formatAmount(csr?.netWorth));

  styleSubSectionHeader(ws, row, "VII.", "Transparency and Disclosures Compliances");
  row += 1;

  row = addPeachPointHeader(
    ws,
    row,
    "30.",
    "Complaints/Grievances on any of the principles (Principles 1 to 9) under the National Guidelines on Responsible Business Conduct:",
    COMPLAINTS_TABLE_END
  );
  row = addComplaintsTable(
    ws,
    row,
    financialYear,
    transparency?.currentFYComplaints,
    transparency?.previousFYComplaints
  );

  row = addLabelValueRow(
    ws,
    row,
    "31.",
    "Overview of the entity's material responsible business conduct issues",
    "View details",
    { link: "'Business conduct issues'" }
  );

  return row;
}

export function buildGenaralDisclosures(workbook, sectionA) {
  const ws = workbook.addWorksheet("General Disclosures", {
    views: [{ showGridLines: true }],
  });

  const columnWidths = {
    A: 2,
    B: 2,
    C: 2,
    D: 8,
    E: 28,
    F: 12,
    G: 12,
    H: 12,
    I: 12,
    J: 12,
    K: 12,
    L: 12,
    M: 20,
    N: 14,
    O: 14,
    P: 14,
    Q: 20,
    R: 12,
    S: 12,
  };
  Object.entries(columnWidths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });

  const li = sectionA?.listedIdentity || {};
  const ops = sectionA?.operations || {};
  const emp = sectionA?.employeesAndWorkers || {};
  const holding = sectionA?.holding || {};
  const transparency = sectionA?.transparencyAndDisclosures || {};
  const qc = li?.queryContact || {};
  const fyRanges = getFinancialYearRanges(li?.financialYear);

  let row = 4;
  styleSectionHeader(ws, row, "SECTION A: GENERAL DISCLOSURES");
  row += 1;

  styleSubSectionHeader(ws, row, "I.", "Details of the listed entity");
  row += 1;

  row = addLabelValueRow(ws, row, "1.", "NSE Symbol", safeText(li?.nseSymbol, "NOTLISTED"), {
    mandatory: true,
  });
  row = addLabelValueRow(ws, row, "2.", "BSE Scrip Code", safeText(li?.bseScripCode), {
    mandatory: true,
  });
  row = addLabelValueRow(ws, row, "3.", "MSEI Symbol", safeText(li?.mseiSymbol, "NOTLISTED"), {
    mandatory: true,
  });
  row = addLabelValueRow(ws, row, "4.", "ISIN", safeText(li?.isin), { mandatory: true });
  row = addLabelValueRow(
    ws,
    row,
    "5.",
    "Corporate Identity Number (CIN)",
    safeText(li?.corporateIdentityNumber)
  );
  row = addLabelValueRow(
    ws,
    row,
    "6.",
    "Name of the Listed Entity",
    safeText(li?.listedCompanyName)
  );
  row = addLabelValueRow(
    ws,
    row,
    "7.",
    "Date of Incorporation",
    li?.yearOfIncorporation ? formatIndianDate(1, 1, li.yearOfIncorporation) : ""
  );
  row = addLabelValueRow(
    ws,
    row,
    "8.",
    "Registered office address",
    safeText(li?.registeredOfficeAddress)
  );
  row = addLabelValueRow(
    ws,
    row,
    "9.",
    "Corporate address",
    safeText(li?.corporateOfficeAddress)
  );
  row = addLabelValueRow(ws, row, "10.", "E-mail", safeText(li?.companyEmail));
  row = addLabelValueRow(ws, row, "11.", "Telephone", safeText(li?.companyTelephoneNumber));
  row = addLabelValueRow(ws, row, "12.", "Website", safeText(li?.companyWebsite));

  row = addFinancialYearBlock(ws, row, fyRanges);

  row = addLabelValueRow(
    ws,
    row,
    "14.",
    "Name of the Stock Exchange(s) where shares are listed",
    "View Details",
    { link: "'Stock exchange'" }
  );
  row = addLabelValueRow(
    ws,
    row,
    "15.",
    "Paid-up Capital (in Rs)",
    formatAmount(li?.paidUpCapital)
  );

  row = addFullWidthLabelRow(
    ws,
    row,
    "Name and contact details (telephone, email address) of the person who may be contacted in case of any queries on the BRSR report",
    "16."
  );

  row = addSubLabelValueRow(ws, row, "Name", safeText(qc?.name));
  row = addSubLabelValueRow(ws, row, "Contact", safeText(qc?.telephone));
  row = addSubLabelValueRow(ws, row, "E mail", safeText(qc?.email));

  row = addLabelValueRow(
    ws,
    row,
    "17.",
    "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together)",
    safeText(li?.reportingBoundary, "Standalone basis")
  );

  const hasAssurance = Boolean(li?.assuranceProvider || li?.assuranceType);
  row = addLabelValueRow(
    ws,
    row,
    "18.",
    "Whether the company has undertaken assessment or assurance of the BRSR Core?",
    hasAssurance ? "Yes" : "No"
  );
  row = addLabelValueRow(
    ws,
    row,
    "19.",
    "Name of assessment or assurance provider",
    safeText(li?.assuranceProvider)
  );
  row = addLabelValueRow(
    ws,
    row,
    "20.",
    "Type of assessment or assurance obtained",
    safeText(li?.assuranceType)
  );

  styleSubSectionHeader(ws, row, "II.", "Products/services");
  row += 1;

  row = addLabelValueRow(
    ws,
    row,
    "21.",
    "Details of business activities (accounting for 90% of the turnover)",
    "View details",
    { link: "'Details of business activities'" }
  );
  row = addLabelValueRow(
    ws,
    row,
    "22.",
    "Products/Services sold by the entity (accounting for 90% of the entity's Turnover)",
    "View details",
    { link: "'Products services sold'" }
  );

  styleSubSectionHeader(ws, row, "III.", "Operations");
  row += 1;

  row = addFullWidthLabelRow(
    ws,
    row,
    "Number of locations where plants and/or operations/offices of the entity are situated",
    "23."
  );

  ws.getCell(row, COL_LABEL).value = "Location";
  ws.getCell(row, COL_LABEL).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_LABEL));
  ws.getCell(row, COL_LABEL).alignment = { horizontal: "left", vertical: "middle" };

  ws.mergeCells(row, COL_VALUE_START, row, COL_DATA_1_END);
  ws.getCell(row, COL_VALUE_START).value = "Number of plants";
  ws.getCell(row, COL_VALUE_START).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_VALUE_START));
  ws.getCell(row, COL_VALUE_START).alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(row, COL_DATA_2_START, row, COL_DATA_2_END);
  ws.getCell(row, COL_DATA_2_START).value = "Number of offices";
  ws.getCell(row, COL_DATA_2_START).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_DATA_2_START));
  ws.getCell(row, COL_DATA_2_START).alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(row, COL_DATA_3_START, row, COL_VALUE_END);
  ws.getCell(row, COL_DATA_3_START).value = "Total";
  ws.getCell(row, COL_DATA_3_START).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_DATA_3_START));
  ws.getCell(row, COL_DATA_3_START).alignment = { horizontal: "center", vertical: "middle" };

  borderCell(ws.getCell(row, COL_NUM));
  row += 1;

  const locationRows = [
    {
      label: "National",
      plants: safeText(ops?.numberOfLocations?.national?.plants, "0"),
      offices: safeText(ops?.numberOfLocations?.national?.offices, "0"),
      total: safeText(ops?.numberOfLocations?.national?.total, "0"),
    },
    {
      label: "International",
      plants: safeText(ops?.numberOfLocations?.international?.plants, "0"),
      offices: safeText(ops?.numberOfLocations?.international?.offices, "0"),
      total: safeText(ops?.numberOfLocations?.international?.total, "0"),
    },
  ];

  locationRows.forEach((item) => {
    borderCell(ws.getCell(row, COL_NUM));
    setLabelCell(ws, row, item.label);

    ws.mergeCells(row, COL_VALUE_START, row, COL_DATA_1_END);
    ws.getCell(row, COL_VALUE_START).value = item.plants;
    borderCell(ws.getCell(row, COL_VALUE_START));

    ws.mergeCells(row, COL_DATA_2_START, row, COL_DATA_2_END);
    ws.getCell(row, COL_DATA_2_START).value = item.offices;
    borderCell(ws.getCell(row, COL_DATA_2_START));

    ws.mergeCells(row, COL_DATA_3_START, row, COL_VALUE_END);
    ws.getCell(row, COL_DATA_3_START).value = item.total;
    borderCell(ws.getCell(row, COL_DATA_3_START));

    [COL_VALUE_START, COL_DATA_2_START, COL_DATA_3_START].forEach((col) => {
      ws.getCell(row, col).font = BODY_FONT;
      ws.getCell(row, col).alignment = { horizontal: "center", vertical: "middle" };
    });
    row += 1;
  });

  row = addFullWidthLabelRow(ws, row, "Markets served by the entity", "24.");
  row = addFullWidthLabelRow(ws, row, "A. Number of locations");

  ws.getCell(row, COL_LABEL).value = "S.No.";
  ws.getCell(row, COL_LABEL).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_LABEL));
  ws.mergeCells(row, COL_VALUE_START, row, COL_VALUE_END);
  ws.getCell(row, COL_VALUE_START).value = "Number";
  ws.getCell(row, COL_VALUE_START).font = { ...BODY_FONT, bold: true };
  borderCell(ws.getCell(row, COL_VALUE_START));
  ws.getCell(row, COL_VALUE_START).alignment = { horizontal: "center", vertical: "middle" };
  borderCell(ws.getCell(row, COL_NUM));
  row += 1;

  const marketRows = [
    {
      label: "National (No. of States)",
      count: safeText(ops?.marketsServed?.nationalStates, "0"),
    },
    {
      label: "International (No. of Countries)",
      count: safeText(ops?.marketsServed?.internationalCountries, "0"),
    },
  ];

  marketRows.forEach((item) => {
    borderCell(ws.getCell(row, COL_NUM));
    setLabelCell(ws, row, item.label);
    ws.mergeCells(row, COL_VALUE_START, row, COL_VALUE_END);
    const countCell = ws.getCell(row, COL_VALUE_START);
    countCell.value = item.count;
    countCell.font = BODY_FONT;
    borderCell(countCell);
    countCell.alignment = { horizontal: "center", vertical: "middle" };
    row += 1;
  });

  borderCell(ws.getCell(row, COL_NUM));
  setLabelCell(
    ws,
    row,
    "B. What is the contribution of exports as a percentage of the total turnover of the entity?"
  );
  setValueCell(ws, row, safeText(ops?.marketsServed?.exportsContribution));
  row += 1;

  borderCell(ws.getCell(row, COL_NUM));
  setLabelCell(ws, row, "C. A brief on types of customers");
  setValueCell(ws, row, safeText(ops?.marketsServed?.customerTypes));

  row = addEmployeesSection(ws, row, emp, li?.financialYear);
  row = addRemainingDisclosuresSection(ws, row, li?.financialYear, holding, transparency);

  return ws;
}
